import Camera from '../../components/Camera';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { StyleSheet, View, Text, TextInput } from 'react-native';
import { FullScreenSpinner } from '../../components/molecules/Spinner';
import GenericButton from '../../components/molecules/GenericButton';
import ProofRequest from '../../features/proof/ProofRequest';
import ProofSuccess from '../../features/proof/ProofSuccess';
import CardsIcon from '../../components/Icons/CardsIcon';
import { useTractionService } from '../../services';
import { CredentialProofRequest } from '../../types/credentials';
import log from '../../logger';

interface ReceiveQRScannerProps {
  setCamera: (value: boolean) => void;
  camera: boolean;
  showSuccess: boolean;
  setShowSuccess: (value: boolean) => void;
}

/**
 * @description Receive QR scanner component
 * @param {ReceiveQRScannerProps} {
 *   setCamera,
 *   camera,
 *   setShowSuccess,
 *   showSuccess
 * }
 * @return {React.FC}
 */
const ReceiveQRScanner: React.FC<ReceiveQRScannerProps> = ({
  setCamera,
  camera,
  setShowSuccess,
  showSuccess
}) => {
  const [qrResult, setQrResult] = useState<string | null | undefined>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showProof, setShowProof] = useState<boolean>(false);
  const [text, setText] = useState('');
  const navigate = useNavigate();
  const service = useTractionService();
  const [proof, setProof] = useState<CredentialProofRequest>({
    name: '',
    issuer: '',
    presentationExchangeId: '',
    requested_attributes: {},
    requested_predicates: {},
    schemaId: '',
    credentialDefinitionId: '',
    referentId: '',
    threadId: '',
    connectionId: '',
    attributes: [],
    state: '',
    role: '',
    requester: '',
    receivedTimeAgo: '',
    credentials: [],
    credentialExchangeId: ''
  });
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);
  const styles = StyleSheet.create({
    container: {
      width: '100%',
      minHeight: '100%',
      maxWidth: 500,
      marginRight: 'auto',
      marginLeft: 'auto'
    },
    cameraContainer: {
      flex: 1,
      display: 'flex'
    },
    input: {
      marginTop: 5,
      height: 40,
      borderColor: '#000',
      borderWidth: 1,
      paddingHorizontal: 10,
      marginBottom: 0,
      borderRadius: 6,
      width: '65%'
    },
    textContainer: {
      flex: 1,
      padding: 0,
      margin: 0,
      flexDirection: 'row',
      justifyContent: 'center'
    }
  });

  useEffect(() => {
    if (qrResult) {
      getRequest(qrResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrResult]);

  const getRequest = async (url: string) => {
    setLoading(true);
    try {
      const request = await service.receiveConnectionlessProofRequest(url);
      await new Promise((resolve) => {
        setProof(request[0]);
        resolve(null);
      });
    } catch (error) {
      log.error('Error fetching proof request:', error);
    } finally {
      setShowProof(true);
      setLoading(false);
    }
  };
  const onAccept = async (item: CredentialProofRequest) => {
    setLoading(true);
    await service.sendProofPresentation(
      item.presentationExchangeId,
      item.referentId,
      item.requested_attributes,
      item.requested_predicates
    );
    setShowProof(false);
    setShowSuccess(true);
    setLoading(false);
  };

  const onDecline = async (item: CredentialProofRequest) => {
    setLoading(true);
    await service.rejectProofRequest(item.presentationExchangeId, 'no thanks');
    setShowProof(false);
    setLoading(false);
    navigate('/');
  };

  const isMobileDevice = (): boolean => {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
  };

  if (loading) {
    return <FullScreenSpinner />;
  }
  //this will need to be genericised to allow for credential offers and.. whatever else can be received
  return (
    <>
      {showSuccess ? (
        <ProofSuccess proof={proof} />
      ) : (
        <>
          {showProof && (
            <ProofRequest
              primaryAction={{
                title: 'Share Credential',
                action: () => onAccept(proof)
              }}
              secondaryAction={{
                title: 'Reject Request',
                action: () => onDecline(proof)
              }}
              proofRequest={proof}
              selectedCredentialIndex={0}
              setSelectedCredentialIndex={() => {}}
            />
          )}
          {camera ? (
            <View style={styles.cameraContainer}>
              <Camera setImage={setQrResult} />
            </View>
          ) : (
            <View
              style={{
                marginTop: 100,
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <CardsIcon iconHeight={200} iconWidth={200} />
              <Text style={{ fontSize: 24, fontWeight: 700, textAlign: 'center' }}>
                Receive an Invitation
              </Text>
              <Text style={{ fontSize: 16, color: '#999', textAlign: 'center' }}>
                Scan a QR Code or input an invitation url.
              </Text>
              {isMobile && (
                <GenericButton
                  title={'Scan QR Code'}
                  onPress={() => setCamera(true)}
                  isPrimary={true}
                  buttonStyle={{
                    backgroundColor: '#fff',
                    width: 140,
                    height: 51,
                    marginTop: 5,
                    borderRadius: 6,
                    borderWidth: 1,
                    borderColor: '#666666',
                    marginRight: 5
                  }}
                  textStyle={{ fontSize: 18, fontWeight: 700, color: '#000' }}
                />
              )}
              <View style={styles.textContainer}>
                <TextInput
                  style={styles.input}
                  value={text}
                  onChangeText={(newText) => {
                    setText(newText);
                  }}
                  scrollEnabled={true}
                  textAlignVertical="center"
                  placeholder="Enter invitation URL here"
                  onSubmitEditing={() => getRequest(text)}
                />
                <GenericButton
                  title="Submit"
                  isPrimary={true}
                  onPress={() => getRequest(text)}
                  buttonStyle={{
                    backgroundColor: '#000',
                    width: 140,
                    height: 40,
                    marginTop: 5,
                    borderRadius: 6,
                    borderWidth: 1,
                    borderColor: '#666666',
                    marginRight: 5
                  }}
                  textStyle={{ fontSize: 18, fontWeight: 700, color: '#fff' }}
                />
              </View>
            </View>
          )}
        </>
      )}
    </>
  );
};

export default ReceiveQRScanner;
